import React from 'react';
import { ChevronDown, Layers, X } from 'lucide-react';
import TextureSelector from '../TextureSelector';
import { FABRIC_TEXTURES } from '../../data/fabricTextures';
import {
  WEAVE_OR_KNIT_OPTIONS,
  FIBER_OPTIONS,
  OPACITY_OPTIONS,
  STRETCH_OPTIONS,
  GSM_WEIGHT_OPTIONS,
  FABRIC_VISUAL_TYPE_OPTIONS,
} from '../../data/fabricAnalysisOptions';

const findTextureByFabricType = (fabricType) => {
  if (!fabricType) return null;
  for (const cat of FABRIC_TEXTURES.fabricTextures || []) {
    const match = cat.types?.find((item) => item.name === fabricType);
    if (match) return { ...match, categoryName: cat.category };
  }
  return { name: fabricType, categoryName: 'Other', description: '' };
};

const splitList = (value) =>
  String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const joinList = (value) => (Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value || '').trim());

const uniqueShadeNames = (...lists) => {
  const names = [];
  lists.flat().forEach((item) => {
    const name =
      typeof item === 'string'
        ? item.trim()
        : String(item?.colorName || item?.hex || '').trim();
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
};

export function snapshotTemplateFabricProperties({
  productDetails,
  extractedColorVariants = [],
  selectedTexture = null,
} = {}) {
  const fa = productDetails?.fabricAnalysis?.fabric_analysis || {};
  const vc = productDetails?.fabricAnalysis?.visual_characteristics || {};
  const fp = productDetails?.fabricAnalysis?.functional_properties || {};
  const fabricProduct = productDetails?.selectedProduct || {};
  const colorways = uniqueShadeNames(
    productDetails?.colorVariants,
    extractedColorVariants,
    Array.isArray(productDetails?.colorShadeNames) ? productDetails.colorShadeNames : []
  );

  return {
    fabric_type: fa.fabric_type || selectedTexture?.name || productDetails?.fabricTexture?.name || '',
    weave_or_knit: fa.weave_or_knit || '',
    fiber_guess: fa.fiber_guess || '',
    opacity: fa.opacity || '',
    stretch: fa.stretch || '',
    gsm_weight: fa.gsm_weight || productDetails?.fabricWeight || '',
    fabricWidth: fabricProduct.width || fabricProduct.fabricWidth || productDetails?.width || '',
    visual_type: vc.visual_type || '',
    primary_colors: joinList(vc.primary_colors),
    secondary_colors: joinList(vc.secondary_colors),
    season_suitability: joinList(fp.season_suitability),
    climate_suitability: joinList(fp.climate_suitability),
    fabricTextureDescription:
      selectedTexture?.description || productDetails?.fabricTexture?.description || '',
    colorways: colorways.join(', '),
  };
}

export function templatePropertiesToGenerationOverrides(draft) {
  if (!draft) return {};
  const compact = (value) => {
    const text = String(value || '').trim();
    return text || undefined;
  };
  const list = (value) => {
    const items = splitList(value);
    return items.length ? items : undefined;
  };

  return {
    fabricAnalysis: {
      fabric_analysis: {
        fabric_type: compact(draft.fabric_type),
        weave_or_knit: compact(draft.weave_or_knit),
        fiber_guess: compact(draft.fiber_guess),
        opacity: compact(draft.opacity),
        stretch: compact(draft.stretch),
        gsm_weight: compact(draft.gsm_weight),
      },
      visual_characteristics: {
        visual_type: compact(draft.visual_type),
        primary_colors: list(draft.primary_colors),
        secondary_colors: list(draft.secondary_colors),
      },
      functional_properties: {
        season_suitability: list(draft.season_suitability),
        climate_suitability: list(draft.climate_suitability),
      },
    },
    fabricWeight: compact(draft.gsm_weight) || null,
    fabricWidth: compact(draft.fabricWidth) || null,
    fabricTexture: compact(draft.fabric_type) || null,
    fabricTextureDescription: compact(draft.fabricTextureDescription) || null,
    opacity: compact(draft.opacity) || null,
    stretch: compact(draft.stretch) || null,
    colorShadeNames: splitList(draft.colorways),
    fabricCatalogUseProvidedShadesOnly: true,
  };
}

function SelectField({ value, options, onChange, placeholder }) {
  const extras = value && !options.includes(value) ? [value] : [];
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 pr-9 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
      >
        <option value="">{placeholder}</option>
        {[...extras, ...options].map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

function PropertyRow({ label, value, onClear, children }) {
  const hasValue = Boolean(String(value || '').trim());
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="text-xs font-medium text-slate-600">{label}</label>
        {hasValue && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-600"
            title="Remove from this catalog (does not change the template or saved fabric)"
          >
            <X size={12} />
            Remove
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Local-only fabric property editor for Generate step 3 when a fabric template is selected.
 * Edits never write back to the fabric template or the saved fabric product.
 */
export default function FabricTemplatePropertiesEditor({ draft, onChange, embedded = false }) {
  if (!draft) return null;

  const patch = (key, value) => onChange?.({ ...draft, [key]: value });
  const clear = (key) => patch(key, '');

  return (
    <div className={embedded ? 'space-y-3' : 'rounded-lg border border-slate-200 bg-white p-3 space-y-3'}>
      {!embedded && (
        <div>
          <div className="flex items-center gap-2">
            <Layers size={15} className="text-indigo-600" />
            <h4 className="text-sm font-semibold text-slate-900">Fabric properties</h4>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Shown on this catalog only. Edit or remove a field to keep it off the generated page — the
            original template and saved fabric are not updated.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PropertyRow label="Fabric type" value={draft.fabric_type} onClear={() => clear('fabric_type')}>
          <TextureSelector
            selectedTexture={findTextureByFabricType(draft.fabric_type)}
            onTextureChange={(texture) => patch('fabric_type', texture ? texture.name : '')}
            showTip={false}
            onTipToggle={() => {}}
            dropdownMode="inline"
          />
        </PropertyRow>
        <PropertyRow label="Weave / knit" value={draft.weave_or_knit} onClear={() => clear('weave_or_knit')}>
          <SelectField
            value={draft.weave_or_knit}
            options={WEAVE_OR_KNIT_OPTIONS}
            onChange={(value) => patch('weave_or_knit', value)}
            placeholder="Select weave/knit"
          />
        </PropertyRow>
        <PropertyRow label="Composition" value={draft.fiber_guess} onClear={() => clear('fiber_guess')}>
          <SelectField
            value={draft.fiber_guess}
            options={FIBER_OPTIONS}
            onChange={(value) => patch('fiber_guess', value)}
            placeholder="Select composition"
          />
        </PropertyRow>
        <PropertyRow label="GSM / weight" value={draft.gsm_weight} onClear={() => clear('gsm_weight')}>
          <SelectField
            value={draft.gsm_weight}
            options={GSM_WEIGHT_OPTIONS}
            onChange={(value) => patch('gsm_weight', value)}
            placeholder="Select GSM weight"
          />
        </PropertyRow>
        <PropertyRow label="Opacity" value={draft.opacity} onClear={() => clear('opacity')}>
          <SelectField
            value={draft.opacity}
            options={OPACITY_OPTIONS}
            onChange={(value) => patch('opacity', value)}
            placeholder="Select opacity"
          />
        </PropertyRow>
        <PropertyRow label="Stretch" value={draft.stretch} onClear={() => clear('stretch')}>
          <SelectField
            value={draft.stretch}
            options={STRETCH_OPTIONS}
            onChange={(value) => patch('stretch', value)}
            placeholder="Select stretch"
          />
        </PropertyRow>
        <PropertyRow label="Pattern" value={draft.visual_type} onClear={() => clear('visual_type')}>
          <SelectField
            value={draft.visual_type}
            options={FABRIC_VISUAL_TYPE_OPTIONS}
            onChange={(value) => patch('visual_type', value)}
            placeholder="Select pattern"
          />
        </PropertyRow>
        <PropertyRow label="Width" value={draft.fabricWidth} onClear={() => clear('fabricWidth')}>
          <input
            type="text"
            value={draft.fabricWidth}
            onChange={(e) => patch('fabricWidth', e.target.value)}
            placeholder="e.g. 44 inches"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <PropertyRow
          label="Texture notes"
          value={draft.fabricTextureDescription}
          onClear={() => clear('fabricTextureDescription')}
        >
          <input
            type="text"
            value={draft.fabricTextureDescription}
            onChange={(e) => patch('fabricTextureDescription', e.target.value)}
            placeholder="Optional texture description"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <PropertyRow
          label="Season"
          value={draft.season_suitability}
          onClear={() => clear('season_suitability')}
        >
          <input
            type="text"
            value={draft.season_suitability}
            onChange={(e) => patch('season_suitability', e.target.value)}
            placeholder="e.g. Summer, All-season"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <PropertyRow
          label="Climate / usage"
          value={draft.climate_suitability}
          onClear={() => clear('climate_suitability')}
        >
          <input
            type="text"
            value={draft.climate_suitability}
            onChange={(e) => patch('climate_suitability', e.target.value)}
            placeholder="e.g. Warm weather, Formal"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <PropertyRow
          label="Primary colors"
          value={draft.primary_colors}
          onClear={() => clear('primary_colors')}
        >
          <input
            type="text"
            value={draft.primary_colors}
            onChange={(e) => patch('primary_colors', e.target.value)}
            placeholder="Comma-separated"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <PropertyRow
          label="Secondary colors"
          value={draft.secondary_colors}
          onClear={() => clear('secondary_colors')}
        >
          <input
            type="text"
            value={draft.secondary_colors}
            onChange={(e) => patch('secondary_colors', e.target.value)}
            placeholder="Comma-separated"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </PropertyRow>
        <div className="sm:col-span-2">
          <PropertyRow label="Colorways" value={draft.colorways} onClear={() => clear('colorways')}>
            <input
              type="text"
              value={draft.colorways}
              onChange={(e) => patch('colorways', e.target.value)}
              placeholder="Comma-separated shade names"
              className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </PropertyRow>
        </div>
      </div>
    </div>
  );
}
